import { FileIcon, FileImage, FileSpreadsheet, FileText } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export type ChatAttachmentSource = 'user' | 'agent'

export type ChatAttachment = {
  id: string
  filename: string
  mimeType: string
  sizeBytes: number
  source: ChatAttachmentSource
  url?: string | null
}

export type ChatFileListItem = ChatAttachment & { createdAt: string }

export type ChatAttachmentUpload = {
  id: string
  uploadUrl: string
  mimeType: string
}

export type ChatAttachmentConfirmation =
  | { status: 'ready'; attachment: ChatAttachment }
  | { status: 'failed'; reason: string }

/** Transport for the composer uploads; the home chat supplies one bound to
 * the workspace's Squad app token. */
export type ChatAttachmentApi = {
  create: (file: File) => Promise<ChatAttachmentUpload>
  confirm: (id: string) => Promise<ChatAttachmentConfirmation>
  remove: (id: string) => Promise<void>
}

export type AttachmentProblem = 'unsupported' | 'too_large' | 'too_many'

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp', '.gif']
const SPREADSHEET_EXTENSIONS = ['.xlsx', '.xls', '.ods', '.csv']
const TEXT_DOCUMENT_EXTENSIONS = [
  '.pdf',
  '.docx',
  '.odt',
  '.txt',
  '.md',
  '.rtf'
]
const DOCUMENT_EXTENSIONS = [
  '.pdf',
  '.docx',
  '.xlsx',
  '.xls',
  '.pptx',
  '.odt',
  '.ods',
  '.odp',
  '.csv',
  '.txt',
  '.md',
  '.json',
  '.xml',
  '.html',
  '.htm',
  '.eml',
  '.rtf'
]

/** Mirrors Squad's CHAT_FILE_* settings so the user gets an instant error. */
export const CHAT_ATTACHMENT_LIMITS = {
  maxFiles: 10,
  maxBytes: 20 * 1024 * 1024,
  imageMaxBytes: 5 * 1024 * 1024
}

export const CHAT_ATTACHMENT_ACCEPT = [
  ...IMAGE_EXTENSIONS,
  ...DOCUMENT_EXTENSIONS
].join(',')

const extensionOf = (name: string) => {
  const dot = name.lastIndexOf('.')

  return dot === -1 ? '' : name.slice(dot).toLowerCase()
}

export const isImageMime = (mime: string) => mime.startsWith('image/')

/** Squad serves only images and PDFs with `Content-Disposition: inline`;
 * everything else downloads, so it is never rendered on the storage host. */
export const opensInline = (mime: string) =>
  isImageMime(mime) || mime === 'application/pdf'

const isImageFile = (file: File) =>
  IMAGE_EXTENSIONS.includes(extensionOf(file.name))

export const validateAttachmentFile = (
  file: File,
  alreadyAttached: number
): AttachmentProblem | null => {
  if (alreadyAttached >= CHAT_ATTACHMENT_LIMITS.maxFiles) return 'too_many'

  const extension = extensionOf(file.name)

  if (
    !IMAGE_EXTENSIONS.includes(extension) &&
    !DOCUMENT_EXTENSIONS.includes(extension)
  ) {
    return 'unsupported'
  }

  const limit = isImageFile(file)
    ? CHAT_ATTACHMENT_LIMITS.imageMaxBytes
    : CHAT_ATTACHMENT_LIMITS.maxBytes

  return file.size > limit ? 'too_large' : null
}

export const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`

  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const attachmentIcon = (filename: string, mimeType = ''): LucideIcon => {
  const extension = extensionOf(filename)

  if (IMAGE_EXTENSIONS.includes(extension) || isImageMime(mimeType)) {
    return FileImage
  }

  if (SPREADSHEET_EXTENSIONS.includes(extension)) return FileSpreadsheet

  if (TEXT_DOCUMENT_EXTENSIONS.includes(extension)) return FileText

  return FileIcon
}
